import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export interface ErrorWithRetryProps {
  error: Error | string
  onRetry?: () => void
  alternativePath?: { label: string; onClick: () => void }
  className?: string
}

function extractMessage(error: Error | string): string {
  if (typeof error === 'string') return error
  return error.message || 'An unexpected error occurred.'
}

export function ErrorWithRetry({ error, onRetry, alternativePath, className }: ErrorWithRetryProps) {
  const { t } = useTranslation()
  // MANDATORY: log original error to console per feedback_no_fake_fallbacks.md.
  // The user-visible message is in addition to, never instead of, the original.
  useEffect(() => {
    console.error('[ErrorWithRetry]', error)
  }, [error])

  const message = extractMessage(error)

  return (
    <div
      className={cn(
        'flex flex-col items-center gap-4 px-6 py-8 text-center',
        // Utility-card step of the radius ladder (DESIGN.md {rounded.lg}) —
        // 10px was a fourth grammar in views that already mixed three.
        'rounded-[18px]',
        'border border-[color:var(--color-error-100)]',
        'bg-[color:var(--color-error-50)]',
        className,
      )}
      role="alert"
      aria-live="assertive"
    >
      <div
        className="text-[color:var(--color-error-500)] text-2xl"
        aria-hidden="true"
      >
        ⚠
      </div>

      <div className="space-y-1">
        <p className="text-[15px] font-semibold text-[color:var(--color-error-700)]">
          {t('common.error')}
        </p>
        <p className="text-[13px] text-[color:var(--color-error-700)] opacity-80 max-w-xs leading-relaxed">
          {message}
        </p>
      </div>

      {(onRetry || alternativePath) && (
        <div className="flex flex-wrap gap-2 justify-center">
          {onRetry && (
            <Button
              variant="outline"
              onClick={onRetry}
              className={cn(
                // min-h-[44px] = iOS minimum tap target.
                'min-h-[44px] border-[color:var(--color-error-300)]',
                'text-[color:var(--color-error-700)]',
                'hover:bg-[color:var(--color-error-100)]',
              )}
            >
              {t('actions.retry')}
            </Button>
          )}
          {alternativePath && (
            <Button
              variant="ghost"
              onClick={alternativePath.onClick}
              className="min-h-[44px] text-[color:var(--color-neutral-600)]"
            >
              {alternativePath.label}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
